import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { indicate } from './operators';
import { Page, PaginatedEndpoint, Sort } from './page';
import { SimpleDataSource } from './simple-datasource';

/**
 * Représente une source de données paginable et triable.
 *
 * c.f. https://medium.com/angular-in-depth/angular-material-pagination-datasource-73080d3457fe
 *
 * @example
 * Exemple d'utilisation avec angular material:
 * ```html
 * <div>
 *   <table mat-table #table
 *       *ngIf="columns"
 *       [dataSource]="dataSource"
 *       matSort
 *       (matSortChange)="sortData($event)"
 *       cdkFocusInitial
 *       >
 *     <ng-container matColumnDef="code">
 *       <th mat-header-cell *matHeaderCellDef mat-sort-header disableClear>Code</th>
 *       <td mat-cell *matCellDef="let element">
 *           {{element.code}}
 *       </td>
 *     </ng-container>
 *     <ng-container matColumnDef="version">
 *       <th mat-header-cell *matHeaderCellDef>Version</th>
 *       <td mat-cell *matCellDef="let element">
 *           {{element.version}}
 *       </td>
 *     </ng-container>
 *     </ng-container>
 *     <tr mat-header-row *matHeaderRowDef="columns"></tr>
 *     <tr mat-row *matRowDef="let row; columns: columns;"></tr>
 *   </table>
 *   <mat-paginator *ngIf="dataSource.page$ | async as page"
 *     [length]="page.totalElements"
 *     [pageSize]="page.size"
 *     [pageIndex]="page.number"
 *     [pageSizeOptions]="[20, 50, 100]"
 *    (page)="dataSource.fetch($event.pageIndex, $event.pageSize)">
 *  </mat-paginator>
 * </div>
 * ```
 */
export class PaginatedDataSource<T, Q = void> implements SimpleDataSource<T> {
  private pageNumber = new Subject<number>();
  private sort!: BehaviorSubject<Sort<T>>;
  private query!: BehaviorSubject<Q>;
  private loading = new Subject<boolean>();

  public page$!: Observable<Page<T>>;
  public loading$ = this.loading.asObservable();

  constructor(
    private endpoint: PaginatedEndpoint<T, Q>,
    initialSort: Sort<T>,
    initialQuery: Q = null as Q,
    public pageSize: number | 0
  ) {
    this.pageSize = pageSize ?? 20;
    this.query = new BehaviorSubject<Q>(initialQuery);
    this.sort = new BehaviorSubject<Sort<T>>(initialSort);
    // const param$ = combineLatest([this.query, this.sort]);
    this.page$ = this.query.pipe(
      switchMap(query =>
        this.pageNumber.pipe(
          startWith(0),
          switchMap(page =>
            this.sort.pipe(
              switchMap(sort =>
                this.endpoint({ page, sort, size: this.pageSize }, query).pipe(
                  indicate(this.loading)
                )
              )
            )
          )
        )
      ),
      shareReplay(1)
    );
  }

  sortBy(sort: Partial<Sort<T>>): void {
    const lastSort = this.sort.getValue();
    const nextSort = { ...lastSort, ...sort };
    this.sort.next(nextSort);
  }

  queryBy(query: Q): void {
    // const lastQuery = this.query.getValue();
    // const nextQuery = {...lastQuery, ...query};
    this.query.next(query);
  }

  /**
   * Méthode à interfacer avec les composants paginator
   *
   * @param page numéro de page à récupérer
   * @param pageSize argument optionnel à transmettre
   * si la taille de la page a changé
   * (utile si le composant paginator permet
   * de changer la taille des pages)
   */
  fetch(page: number, pageSize?: number): void {
    if (pageSize != null) {
      this.pageSize = pageSize;
    }
    this.pageNumber.next(page);
  }

  connect(): Observable<T[]> {
    return this.page$.pipe(map(page => page.content));
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  disconnect(): void {}
}
